import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Chip, Typography, makeStyles } from "@material-ui/core";
import { Document, Page, pdfjs } from "react-pdf";
import ArrowForwardIosRoundedIcon from "@material-ui/icons/ArrowForwardIosRounded";
import ArrowBackIosRoundedIcon from "@material-ui/icons/ArrowBackIosRounded";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import FullscreenExitIcon from "@material-ui/icons/FullscreenExit";
import clsx from "clsx";
import PropTypes from "prop-types";
import FlexboxHorizontal from "../../../SharedComponents/FlexboxHorizontal.jsx";
import "react-pdf/dist/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

const useStyles = makeStyles(() => ({
  pdfTitle: {
    fontWeight: 600,
    textAlign: "center",
  },
  pdfToolbar: {
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
    alignItems: "center",
  },
  pdfToolbarMargin: {
    marginTop: 10,
  },
  fullscreenButton: {
    minWidth: 36,
    backgroundColor: "rgba(230, 230, 230, 0.4)",
    marginLeft: 10,
    "&:hover": {
      backgroundColor: "rgba(230, 230, 230, 0.9)",
    },
  },
  fullscreenDoc: {
    overflowX: "auto",
  },
}));

/**
 * Возвращает заголовок pdf-файла — имя файла без расширения .pdf.
 *
 * @param {string} name - имя файла.
 * @returns {string} заголовок pdf-блока.
 */
export const makePdfTitle = (name) => (name || "").replace(/\.pdf$/i, "");

/**
 * Отдельный pdf-файл теории урока: заголовок с именем файла, постраничный просмотр
 * с кнопками навигации и полноэкранным режимом.
 *
 * @param {Object} props
 * @param {Object} props.pdf - pdf-файл теории ({name, url}).
 * @param {string} [props.id] - идентификатор блока (используется как якорь для прокрутки).
 * @returns {*} разметка pdf-блока теории.
 * @constructor
 */
const PdfTheory = ({ pdf, id }) => {
  const classes = useStyles();
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfWrapperMinHeight, setPdfWrapperMinHeight] = useState(50);
  const [isHorizontal, setIsHorizontal] = useState(false); //Параметр для страницы pdf для корректного отображения в полноэкранном режиме
  const [isFullscreen, setIsFullscreen] = useState(false);
  /** @type {React.MutableRefObject<HTMLElement>} */
  const pdfWrapperRef = useRef();

  // Отслеживаем полноэкранный режим именно для этого pdf-блока, т.к. pdf-файлов
  // на странице может быть несколько
  useEffect(() => {
    /** */
    const fullscreenEventHandler = () => {
      setIsFullscreen(document.fullscreenElement === pdfWrapperRef.current);
    };

    document.addEventListener("fullscreenchange", fullscreenEventHandler);

    return () => {
      document.removeEventListener("fullscreenchange", fullscreenEventHandler);
    };
  }, []);

  /** */
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  /**
   * @param {Number} increment
   * @returns {function(...[*]=)}
   */
  const changePage = (increment) => () => {
    const newPageNumber = currentPage + increment;
    if (newPageNumber < 1 || newPageNumber > numPages) {
      return;
    }
    setCurrentPage(newPageNumber);
  };

  /** */
  const onPdfPageSuccessfullyRendered = (page) => {
    if (!isFullscreen) {
      setPdfWrapperMinHeight(pdfWrapperRef.current.getBoundingClientRect().height);
    }
    setIsHorizontal(page.width > page.height);
  };

  // Имя файла без расширения для заголовка блока
  const pdfTitle = makePdfTitle(pdf.name);

  return (
    <Box id={id} width="855px" margin="20px 0 50px">
      {pdfTitle && (
        <Typography className={classes.pdfTitle} component="h3" variant="subtitle1" gutterBottom>
          {pdfTitle}
        </Typography>
      )}
      <FlexboxHorizontal
        ref={pdfWrapperRef}
        data-tag="pdf wrapper"
        width="100%"
        justifyContent="space-between"
        position="relative"
        minHeight={pdfWrapperMinHeight}
      >
        {numPages && (
          <Button color="primary" disabled={currentPage === 1} onClick={changePage(-1)}>
            <ArrowBackIosRoundedIcon />
          </Button>
        )}
        <Document
          className={clsx({ [classes.fullscreenDoc]: isFullscreen })}
          file={pdf.url}
          onLoadSuccess={onDocumentLoadSuccess}
        >
          <Page
            pageNumber={currentPage}
            width={
              isFullscreen
                ? isHorizontal
                  ? pdfWrapperRef.current.getBoundingClientRect().width - 150
                  : undefined
                : 600
            }
            height={
              isFullscreen
                ? isHorizontal
                  ? undefined
                  : pdfWrapperRef.current.getBoundingClientRect().height
                : undefined
            }
            renderAnnotationLayer={true}
            renderTextLayer={false}
            onRenderSuccess={onPdfPageSuccessfullyRendered}
          />
        </Document>
        {numPages && (
          <>
            <Button color="primary" disabled={currentPage === numPages} onClick={changePage(1)}>
              <ArrowForwardIosRoundedIcon />
            </Button>
            <FlexboxHorizontal className={clsx(classes.pdfToolbar, { [classes.pdfToolbarMargin]: isFullscreen })}>
              <Chip label={`${currentPage} / ${numPages}`} />
              {!isFullscreen && (
                <Button
                  className={classes.fullscreenButton}
                  onClick={() => pdfWrapperRef.current.requestFullscreen().then()}
                >
                  <FullscreenIcon />
                </Button>
              )}
              {isFullscreen && (
                <Button className={classes.fullscreenButton} onClick={() => document.exitFullscreen()}>
                  <FullscreenExitIcon />
                </Button>
              )}
            </FlexboxHorizontal>
          </>
        )}
      </FlexboxHorizontal>
    </Box>
  );
};

PdfTheory.propTypes = {
  pdf: PropTypes.shape({
    name: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
  id: PropTypes.string,
};

export default PdfTheory;
